import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { TuhaWordmark } from "@/components/TuhaLogo";

const UPDATED = "June 2026";

function Shell({ title, children, testid }) {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-background grain" data-testid={testid}>
      <header className="px-6 md:px-12 py-6 flex items-center justify-between max-w-3xl mx-auto">
        <button onClick={() => nav("/")} className="flex items-center gap-2" data-testid="legal-home-link">
          <TuhaWordmark size="sm" />
        </button>
        <button onClick={() => nav(-1)} className="inline-flex items-center gap-1 text-sm text-foreground/70 hover:text-foreground transition">
          <ArrowLeft size={16} /> Back
        </button>
      </header>
      <main className="max-w-3xl mx-auto px-6 md:px-12 pb-20">
        <h1 className="font-serif-display text-4xl sm:text-5xl text-foreground tracking-tight">{title}</h1>
        <p className="text-xs text-muted-foreground mt-3 mb-8">Last updated: {UPDATED}</p>
        <div className="space-y-6 text-sm text-foreground/80 leading-relaxed [&_h2]:font-serif-display [&_h2]:text-xl [&_h2]:text-primary [&_h2]:mt-8 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1">
          {children}
        </div>
      </main>
    </div>
  );
}

export function Terms() {
  return (
    <Shell title="Terms & Conditions" testid="terms-page">
      <p>Welcome to KAAYA. By creating an account or using the service, you agree to these Terms & Conditions. Please read them carefully.</p>

      <h2>1. The Service</h2>
      <p>KAAYA is an AI-powered health and nutrition assistant offering calorie, workout and meal tracking, personalised recipe curation, meal planning and related features through a conversational, multi-agent interface. Features marked "coming soon" (such as Pantry &amp; Cart Management) may not yet be available.</p>

      <h2>2. AI Advisory — Important</h2>
      <p>KAAYA relies on artificial intelligence and large language models. <strong>AI can make mistakes, produce inaccurate or incomplete information, and is not a substitute for professional advice.</strong> All nutrition, calorie, recipe and wellness guidance is provided for general informational purposes only and is <strong>not medical, dietary, or healthcare advice</strong>. Always exercise your own discretion and consult a qualified doctor, dietitian or healthcare professional before making decisions about your diet, exercise, medication or health conditions — especially if you have allergies, medical conditions, or are pregnant. You are solely responsible for how you use the information provided.</p>

      <h2>3. Eligibility & Accounts</h2>
      <p>You must be at least 18 years old (or have guardian consent) to use KAAYA. You are responsible for the accuracy of the information you provide and for maintaining the security of your account, which is accessed via Google Sign-In.</p>

      <h2>4. Subscriptions & Payments</h2>
      <ul>
        <li>KAAYA offers time-based access plans. Every new account starts with a free trial.</li>
        <li>Payments are processed in INR. <strong>All payments are final and non-refundable.</strong></li>
        <li>Purchasing a new plan while one is active extends your remaining access. Lifetime access lasts as long as KAAYA (the company and product) remains operational.</li>
        <li>Access to paid features ends when your plan lapses.</li>
      </ul>

      <h2>5. Acceptable Use</h2>
      <p>You agree not to misuse the service, attempt to reverse-engineer the agents or models, scrape data, or use KAAYA for any unlawful purpose. We may suspend accounts that violate these terms.</p>

      <h2>6. Intellectual Property</h2>
      <p>KAAYA, its branding, software and content are owned by KAAYA. Recipes and plans generated for you may be used for your personal, non-commercial purposes.</p>

      <h2>7. Limitation of Liability</h2>
      <p>To the maximum extent permitted by law, KAAYA is provided "as is" without warranties of any kind. KAAYA and its operators are not liable for any health outcomes, damages or losses arising from reliance on AI-generated guidance or use of the service.</p>

      <h2>8. Changes</h2>
      <p>We may update these terms from time to time. Continued use of KAAYA after changes constitutes acceptance of the revised terms.</p>

      <h2>9. Contact</h2>
      <p>For questions about these terms, reach us through the in-app Support & Feedback option.</p>
    </Shell>
  );
}

export function Privacy() {
  return (
    <Shell title="Privacy Policy" testid="privacy-page">
      <p>This Privacy Policy explains how KAAYA collects, uses and protects your information.</p>

      <h2>1. Information We Collect</h2>
      <ul>
        <li><strong>Account data:</strong> name, email and profile picture from Google Sign-In.</li>
        <li><strong>Health & profile data:</strong> details you provide such as age, gender, height, weight, goals, dietary preferences, allergies, medical conditions and location.</li>
        <li><strong>Usage data:</strong> meals, workouts, water, recipes, chats and activity within the app.</li>
      </ul>

      <h2>2. How We Use Your Data</h2>
      <p>We use your data to operate the service, personalise recommendations through our AI agents, track your nutrition and progress, process payments, and improve KAAYA. Your profile is used to make recommendations tailored to your body type, demographics, goals and preferences.</p>

      <h2>3. AI Processing</h2>
      <p>To generate recommendations, relevant parts of your profile and queries are processed by third-party AI/LLM providers. We share only what is necessary to deliver the feature you requested. AI outputs may be inaccurate; see the AI advisory in our Terms.</p>

      <h2>4. Data Sharing</h2>
      <p>We do not sell your personal data. We share data only with service providers required to run KAAYA (authentication, AI processing, payments, hosting) and where required by law.</p>

      <h2>5. Data Security & Retention</h2>
      <p>We take reasonable measures to protect your data and retain it for as long as your account is active or as needed to provide the service and meet legal obligations.</p>

      <h2>6. Your Rights</h2>
      <p>You may request access to or deletion of your data by contacting us through the in-app Support & Feedback option.</p>

      <h2>7. Changes</h2>
      <p>We may update this policy from time to time. Material changes will be reflected by the "last updated" date above.</p>
    </Shell>
  );
}
